import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Filters from "./filters/Filters";
import Listings from "../listings/Listings";
import styles from "./SearchPage.module.css";
import { API_BASE_URL } from "configs/AppConfig";
import useWindowWidth from "helpers/useWindowWidth";

const SearchPageLatest = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );
  const width = useWindowWidth();

  // Use URL limit if present, otherwise use dynamic width-based limit
  const dynamicLimit = width > 1600 ? 12 : width > 1200 ? 12 : 8;
  const limit = Number(searchParams.get("limit")) || dynamicLimit;
  const [paginationChange, setPaginationChange] = useState({
    page: Number(searchParams.get("page")) || 1,
    limit: limit,
  });

  // Drawer state for mobile filters
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const drawerRef = useRef(null);

  // Loading state
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Access Redux location data
  const reduxLocation = useSelector((state) => state.filter);

  // Track previous filter params to detect changes
  const [prevFilterParams, setPrevFilterParams] = useState({
    search: searchParams.get("search"),
    category: searchParams.get("category"),
    eventType: searchParams.get("eventType"),
    city: searchParams.get("city"),
  });

  // Handle outside click to close drawer
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDrawerOpen]);

  // Toggle drawer
  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  // Close drawer
  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    // Current filter params
    const currentFilterParams = {
      search: searchParams.get("search"),
      category: searchParams.get("category"),
      eventType: searchParams.get("eventType"),
      city: searchParams.get("city"),
    };

    // Check if any filter has changed
    const hasFilterChanged =
      currentFilterParams.search !== prevFilterParams.search ||
      currentFilterParams.category !== prevFilterParams.category ||
      currentFilterParams.eventType !== prevFilterParams.eventType ||
      currentFilterParams.city !== prevFilterParams.city;

    // Reset page only if a filter has changed
    if (hasFilterChanged) {
      setCurrentPage(1);
      setPaginationChange((prev) => ({ ...prev, page: 1 }));
      setPrevFilterParams(currentFilterParams); // Update previous filter params
    }

    // Update URL params
    setSearchParams(
      {
        page: hasFilterChanged ? "1" : searchParams.get("page") || "1",
        limit: searchParams.get("limit") || limit.toString(),
        ...(searchParams.get("search") && {
          search: searchParams.get("search"),
        }),
        ...(searchParams.get("category") && {
          category: searchParams.get("category"),
        }),
        ...(searchParams.get("eventType") && {
          eventType: searchParams.get("eventType"),
        }),
        ...(searchParams.get("city") && { city: searchParams.get("city") }),
      },
      { replace: true }
    );
  }, [
    searchParams.get("search"),
    searchParams.get("category"),
    searchParams.get("eventType"),
    searchParams.get("city"),
    limit,
    setSearchParams,
  ]);

  useEffect(() => {
    // Fetch listings when URL params change
    const fetchListings = async () => {
      setListings([]);
      setInitialLoad(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      try {
        setLoading(true);
        const params = new URLSearchParams(searchParams);

        // City logic implementation
        const cityFromUrl = searchParams.get("city");
        const hasOtherFilters =
          searchParams.get("search") ||
          searchParams.get("category") ||
          searchParams.get("eventType");

        // Remove city from params first to handle it conditionally
        params.delete("city");

        // City logic:
        // 1. If city is coming in URL, pass it to listing API
        // 2. If no filter in URL, take location from Redux and pass city from Redux location
        // 3. If any other filter coming in URL other than city, don't pass city in API
        if (cityFromUrl) {
          params.set("city", cityFromUrl);
        } else if (!hasOtherFilters && reduxLocation?.place) {
          params.set("city", reduxLocation.place);
        }

        const response = await fetch(
          `${API_BASE_URL}listing/get/all/public?${params.toString()}`
        );
        const data = await response.json();
        setListings(data.data || []);
        setTotalItems(data.total || 0);
        setLoading(false);
        setInitialLoad(false);
      } catch (error) {
        console.error("Error fetching listings:", error);
        setLoading(false);
        setInitialLoad(false);
      }
    };
    fetchListings();
  }, [searchParams, reduxLocation?.place]);

  // Update paginationChange when limit changes due to window resize (only if no URL limit)
  useEffect(() => {
    // Only update limit from window resize if there's no limit in URL
    if (!searchParams.get("limit")) {
      setPaginationChange((prev) => ({ ...prev, limit: dynamicLimit }));
    }
  }, [dynamicLimit, searchParams]);

  useEffect(() => {
    // Update URL when paginationChange changes
    setSearchParams(
      {
        ...Object.fromEntries(searchParams),
        page: paginationChange.page.toString(),
        limit: paginationChange.limit.toString(),
      },
      { replace: true }
    );
  }, [paginationChange, setSearchParams]);

  return (
    <div className={styles.container}>
      {/* Desktop Filters */}
      <div className={styles.desktopFilters}>
        <Filters
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          limit={limit}
        />
      </div>

      {/* Mobile Filter Button */}
      <div className={styles.mobileFilterButton}>
        <button onClick={toggleDrawer} className={styles.filterButton}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 7H21L19 9H5L3 7ZM6 11H18L16 13H8L6 11ZM9 15H15L13 17H11L9 15Z"
              fill="currentColor"
            />
          </svg>
          Filters
        </button>
      </div>

      {/* Mobile Drawer Overlay */}
      {isDrawerOpen && <div className={styles.drawerOverlay} />}

      {/* Mobile Drawer */}
      <div
        ref={drawerRef}
        className={`${styles.drawer} ${isDrawerOpen ? styles.drawerOpen : ""}`}
      >
        <div className={styles.drawerHeader}>
          <h3>Filters</h3>
          <button onClick={closeDrawer} className={styles.closeButton}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className={styles.drawerContent}>
          <Filters
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            limit={limit}
            isDrawer={true}
            closeDrawer={closeDrawer}
          />
        </div>
      </div>

      <Listings
        listings={listings}
        totalItems={totalItems}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        paginationChange={paginationChange}
        setPaginationChange={setPaginationChange}
        limit={limit}
        loading={loading}
        initialLoad={initialLoad}
      />
    </div>
  );
};

export default SearchPageLatest;
